import { Component } from 'react'
import { REPORTS_LIST_PATH } from '@config/reportRoutes'

/**
 * Catches errors thrown inside a single report page so one broken
 * specialized renderer can't take down the whole app. Surfaces the
 * error message + stack inline so we can debug from the live site
 * instead of redirecting to home.
 */
class ReportErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[ReportErrorBoundary]', error, info)
    this.setState({ info })
  }

  reset = () => this.setState({ hasError: false, error: null, info: null })

  render() {
    if (!this.state.hasError) return this.props.children
    const { error, info } = this.state
    const stack = info?.componentStack || error?.stack || ''
    return (
      <div className="max-w-3xl mx-auto py-12">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">This report failed to render</h2>
              <p className="text-sm text-gray-500 mt-1">{error?.message || 'Unknown error'}</p>
            </div>
          </div>

          {stack && (
            <details className="mt-4 text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Show stack trace</summary>
              <pre className="mt-2 p-3 bg-gray-50 border border-gray-100 rounded overflow-auto max-h-64 text-[11px] text-gray-600 whitespace-pre-wrap">
                {stack.trim()}
              </pre>
            </details>
          )}

          <div className="flex gap-2 mt-6">
            <button
              onClick={this.reset}
              className="px-4 py-2 text-sm font-medium bg-cb-accent text-white rounded-lg hover:bg-cb-accent/90"
            >
              Try again
            </button>
            <a
              href={REPORTS_LIST_PATH}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Back to reports
            </a>
          </div>
        </div>
      </div>
    )
  }
}

export default ReportErrorBoundary
